import axios, { AxiosError } from "axios";
import { useAuthContext } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export interface IAuthHttp {
    doGet<TResponse>(path: string): Promise<TResponse>;
    doPost<TBody, TResult>(path: string, body: TBody): Promise<{ result: TResult|null; status: number; }|null>;
    doPut<TBody>(path: string, body: TBody): Promise<TBody>;
    doDelete(path: string): Promise<Response | null>;
    doPatch<TBody, TResult>(path: string, body: TBody): Promise<{ result: TResult|null; status: number; }|null>;
}

const useAuthHttp = (): IAuthHttp => {
    const auth = useAuthContext();
    const navigate = useNavigate();

    async function doGet<TResponse>(path: string): Promise<TResponse> {
        try {
            const response = await axios.get<TResponse>(`${import.meta.env.VITE_API_URL}/${path.startsWith('/') ? path.substring(1) : path}`, {
                headers: {
                    Authorization: auth.token
                }
            });

            return {...response.data, status: response.status};
        } catch (err) {
            const axiosError = err as AxiosError;
            if ((axiosError.response?.status == 502)) {
                navigate("/bad-gateway");
            }

            if (axiosError.response?.status == 401) {
                await auth.logout();
            }

            throw err;
        }
    }

    async function doPost<TBody, TResult>(path: string, body: TBody) {
        return doSomething<TBody, TResult>(path, body, "POST");
    }
    
    async function doPatch<TBody, TResult>(path: string, body: TBody) {
        return doSomething<TBody, TResult>(path, body, "PATCH");
    }

    async function doPut<TBody>(path: string, body: TBody) {
        const headers = await getHeaders();
        if (!headers) return null;

        const result = await fetch(
            `${import.meta.env.VITE_API_URL}/${path.startsWith('/') ? path.substring(1) : path}`, 
            {
                method: "PUT",
                body: JSON.stringify(body),
                headers
            });

        if (result.status == 401) {
            await auth.logout();
            return null;
        }

        if (result.status >= 500) {
            console.error(result);
            navigate("/bad-gateway");
            return null;
        }

        return await result.json();
    }

    async function doDelete(path: string) {
        const headers = await getHeaders();
        if (!headers) return null;

        const result = await fetch(
            `${import.meta.env.VITE_API_URL}/${path.startsWith('/') ? path.substring(1) : path}`, 
            {
                method: "DELETE",
                headers
            });

        if (result.status == 401) {
            await auth.logout();
            return null;
        }

        if (result.status >= 500) {
            console.error(result);
            navigate("/bad-gateway");
            
            return null;
        }

        return result;
    }

    async function doSomething<TBody, TResult>(path: string, body: TBody, method: string): Promise<{ result: TResult|null; status: number; }|null> {
        const headers = await getHeaders();
        if (!headers) return null;

        const result = await fetch(
            `${import.meta.env.VITE_API_URL}/${path.startsWith('/') ? path.substring(1) : path}`, 
            {
                method,
                body: JSON.stringify(body),
                headers
            });

        if (result.status == 401) {
            await auth.logout();
            return null;
        }

        if (result.status >= 400 && result.status < 500) {
            throw new Error((await result.json())?.message ?? "Erro ao processar a requisição");
        }

        if (result.status >= 500) {
            console.error(result);
            navigate("/bad-gateway");
            
            return null;
        }

        let responseResult;
        try {
            responseResult = await result.json();
        } catch {
            responseResult = null;
        }

        return { result: responseResult, status: result.status };
    }

    const getHeaders = async () => {
        if (!auth.token) {
            await auth.logout();
            return undefined;
        }

        return {
            "Content-Type": "application/json",
            "Authorization": auth.token 
        };
    }
    
    return { doGet, doPost, doPut, doDelete, doPatch };
}

export default useAuthHttp;
